import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import apiConfig from "../../config/apiConfig";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchForeignResource } from "../../apis/resources";
import { fetchEnum } from "../../apis/enum";
import { useParams } from "react-router-dom";
import { useDriveViewModel } from "../../viewModels/useDriveViewModel";
import { LoginContext } from "../../context/LoginContext";
import { apiFetch } from "../../apis/apiFetch";
import styles from "../Styles/CreateCertificate.module.css";

// ---------- Date helpers ----------
const toDate = (yyyyMmDd: string) => new Date(`${yyyyMmDd}T00:00:00`);

const addDays = (yyyyMmDd: string, days: number) => {
  const d = toDate(yyyyMmDd);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const isStrictBefore = (start?: string, end?: string) => {
  if (!start || !end) return true;
  return toDate(start).getTime() < toDate(end).getTime();
};

const CreateDrive = () => {
  const { user } = useContext(LoginContext);

  const [showToast, setShowToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const queryClient = useQueryClient();

  const { appId }: any = useParams<any>();

  const {
    setFields,
    enums,
    setEnums,
    setForeignKeyData,
    dataToSave,
    setDataToSave,
    loadMetadata,
    save,
  } = useDriveViewModel(user?.userId || "", appId);

  useEffect(() => {
    loadMetadata();
  }, []);

  // ---------- Metadata ----------
  useQuery({
    queryKey: ["resMetaData", "driveCreate"],
    queryFn: async () => {
      const res = await apiFetch(apiConfig.getResourceMetaDataUrl("Drive"));
      const data = await res.json();
      setFields(data[0].fieldValues);

      for (const field of data[0].fieldValues.filter((f: any) => f.foreign)) {
        if (!fetchedResources.current.has(field.foreign)) {
          fetchedResources.current.add(field.foreign);
          const foreignData = await fetchForeignResource(field.foreign);
          setForeignKeyData((prev: any) => ({
            ...prev,
            [field.foreign]: foreignData,
          }));
        }
      }

      for (const field of data[0].fieldValues.filter((f: any) => f.isEnum)) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);
          const enumData = await fetchEnum(field.possible_value);
          setEnums((prev: any) => ({
            ...prev,
            [field.possible_value]: enumData,
          }));
        }
      }

      return data;
    },
  });

  // ---------- Date values ----------
  const startVal = dataToSave["startdate"] || "";
  const endVal = dataToSave["enddate"] || "";

  const editStartVal = dataToSave["editstartdate"] || "";
  const editEndVal = dataToSave["editenddate"] || "";

  // ---------- Validations ----------
  const dateValid = isStrictBefore(startVal, endVal);
  const editDateValid = isStrictBefore(editStartVal, editEndVal);

  // ---------- Min / Max ----------
  const endMin = useMemo(
    () => (startVal ? addDays(startVal, 1) : undefined),
    [startVal]
  );

  const startMax = useMemo(
    () => (endVal ? addDays(endVal, -1) : undefined),
    [endVal]
  );

  const editEndMin = useMemo(
    () => (editStartVal ? addDays(editStartVal, 1) : undefined),
    [editStartVal]
  );

  const editStartMax = useMemo(
    () => (editEndVal ? addDays(editEndVal, -1) : undefined),
    [editEndVal]
  );

  // ---------- Error handler ----------
  const showError = (msg: string) => {
    setErrorMsg(msg);
    setShowErrorToast(true);
  };

  // ---------- Submit ----------
  const handleCreate = async () => {
    if (!startVal || !endVal)
      return showError("Start and End dates are required");

    if (!dataToSave["drivetype"])
      return showError("Drive type is required");

    if (!dateValid)
      return showError("Start date must be before End date");

    if (!editDateValid)
      return showError("Edit start date must be before Edit end date");


    try {
      await save();
      setShowToast(true);
      queryClient.invalidateQueries({
        queryKey: ["resourceData", "driveCreate"],
      });
      setDataToSave({});
    } catch (err: any) {
      showError(err.message || "Failed to create drive");
    }
  };

  // ---------- Submit condition ----------
  const canSubmit =
    (dataToSave["name"] || "").trim() &&
    dataToSave["drivetype"] &&
    startVal &&
    endVal &&
    dateValid;

  return (
    <div className={styles.batchformCard}>
      <div className={styles.certificateFormWrapper}>
        <h2 className={styles.sectionTitle}>Create Drive</h2>

        <div className={styles.formGrid}>
          {/* Name */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <span className={styles.required}>*</span> Name
            </label>
            <input
              className={styles.formControl}
              value={dataToSave["name"] || ""}
              onChange={(e) =>
                setDataToSave({ ...dataToSave, name: e.target.value })
              }
            />
          </div>

          {/* Drive Type */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <span className={styles.required}>*</span> Drive Type
            </label>
            <select
              className={styles.formControl}
              value={dataToSave["drivetype"] || ""}
              onChange={(e) =>
                setDataToSave({ ...dataToSave, drivetype: e.target.value })
              }
            >
              <option value="">Select Drive Type</option>
              {enums["Drivetype"]?.map((val: any, idx: any) => (
                <option key={idx} value={val}>
                  {val}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <span className={styles.required}>*</span> Start Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={startVal ? dayjs(startVal) : null}
                maxDate={startMax ? dayjs(startMax) : undefined}
                onChange={(newValue) => {
                  setDataToSave((prev: any) => ({
                    ...prev,
                    startdate: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                  }));
                }}
                slotProps={{
                  textField: {
                    placeholder: "dd-mm-yyyy",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        padding: '0px 14px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        "& fieldset": {
                          borderColor: '#d0d0d0',
                          borderWidth: '1px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        },
                        "&:hover fieldset": { borderColor: '#d0d0d0' },
                        "&.Mui-focused fieldset": {
                          borderColor: '#2196f3',
                          boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                          borderWidth: '1px'
                        }
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: '10px 0px',
                        fontSize: '14px',
                        color: '#333',
                        fontFamily: 'inherit',
                        height: 'auto',
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/* End Date */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              <span className={styles.required}>*</span> End Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={endVal ? dayjs(endVal) : null}
                minDate={endMin ? dayjs(endMin) : undefined}
                onChange={(newValue) => {
                  setDataToSave((prev: any) => ({
                    ...prev,
                    enddate: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                  }));
                }}
                slotProps={{
                  textField: {
                    placeholder: "dd-mm-yyyy",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        padding: '0px 14px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        "& fieldset": {
                          borderColor: '#d0d0d0',
                          borderWidth: '1px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        },
                        "&:hover fieldset": { borderColor: '#d0d0d0' },
                        "&.Mui-focused fieldset": {
                          borderColor: '#2196f3',
                          boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                          borderWidth: '1px'
                        }
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: '10px 0px',
                        fontSize: '14px',
                        color: '#333',
                        fontFamily: 'inherit',
                        height: 'auto',
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/*JEE Advanced Edit Start Date */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              JEE Advanced Edit Start Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={editStartVal ? dayjs(editStartVal) : null}
                maxDate={editStartMax ? dayjs(editStartMax) : undefined}
                onChange={(newValue) => {
                  setDataToSave((prev: any) => ({
                    ...prev,
                    editstartdate: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                  }));
                }}
                slotProps={{
                  textField: {
                    placeholder: "dd-mm-yyyy",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        padding: '0px 14px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        "& fieldset": {
                          borderColor: '#d0d0d0',
                          borderWidth: '1px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        },
                        "&:hover fieldset": { borderColor: '#d0d0d0' },
                        "&.Mui-focused fieldset": {
                          borderColor: '#2196f3',
                          boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                          borderWidth: '1px'
                        }
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: '10px 0px',
                        fontSize: '14px',
                        color: '#333',
                        fontFamily: 'inherit',
                        height: 'auto',
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/* JEE Advanced Edit End Date */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              JEE Advanced Edit End Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={editEndVal ? dayjs(editEndVal) : null}
                minDate={editEndMin ? dayjs(editEndMin) : undefined}
                onChange={(newValue) => {
                  setDataToSave((prev: any) => ({
                    ...prev,
                    editenddate: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                  }));
                }}
                slotProps={{
                  textField: {
                    placeholder: "dd-mm-yyyy",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        padding: '0px 14px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        "& fieldset": {
                          borderColor: '#d0d0d0',
                          borderWidth: '1px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        },
                        "&:hover fieldset": { borderColor: '#d0d0d0' },
                        "&.Mui-focused fieldset": {
                          borderColor: '#2196f3',
                          boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                          borderWidth: '1px'
                        }
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: '10px 0px',
                        fontSize: '14px',
                        color: '#333',
                        fontFamily: 'inherit',
                        height: 'auto',
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/* Editing End Date */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              Editing End Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={dataToSave["alloweditingenddate"] ? dayjs(dataToSave["alloweditingenddate"]) : null}
                onChange={(newValue) => {
                  setDataToSave((prev: any) => ({
                    ...prev,
                    alloweditingenddate: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                  }));
                }}
                slotProps={{
                  textField: {
                    placeholder: "dd-mm-yyyy",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        padding: '0px 14px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        "& fieldset": {
                          borderColor: '#d0d0d0',
                          borderWidth: '1px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        },
                        "&:hover fieldset": { borderColor: '#d0d0d0' },
                        "&.Mui-focused fieldset": {
                          borderColor: '#2196f3',
                          boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                          borderWidth: '1px'
                        }
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: '10px 0px',
                        fontSize: '14px',
                        color: '#333',
                        fontFamily: 'inherit',
                        height: 'auto',
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/* JEE Editing End Date */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>
              JEE Verification Editing End Date
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MM-YYYY"
                value={dataToSave["jee_editing_end_date"] ? dayjs(dataToSave["jee_editing_end_date"]) : null}
                onChange={(newValue) => {
                  setDataToSave((prev: any) => ({
                    ...prev,
                    jee_editing_end_date: newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : ""
                  }));
                }}
                slotProps={{
                  textField: {
                    placeholder: "dd-mm-yyyy",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        padding: '0px 14px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        "& fieldset": {
                          borderColor: '#d0d0d0',
                          borderWidth: '1px',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                        },
                        "&:hover fieldset": { borderColor: '#d0d0d0' },
                        "&.Mui-focused fieldset": {
                          borderColor: '#2196f3',
                          boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                          borderWidth: '1px'
                        }
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: '10px 0px',
                        fontSize: '14px',
                        color: '#333',
                        fontFamily: 'inherit',
                        height: 'auto',
                      }
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </div>

          {/* Details */}
          <div className={`${styles.formGroup} ${styles.fullWidth}`}>
            <label className={styles.formLabel}>Details</label>
            <textarea
              className={styles.textareaControl}
              value={dataToSave["details"] || ""}
              onChange={(e) =>
                setDataToSave({ ...dataToSave, details: e.target.value })
              }
              rows={3}
            />
          </div>

          {/* BTech Capacity Limits */}
          {dataToSave["drivetype"] === "Btech" && (
            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <h3 className={styles.sectionTitle} style={{ fontSize: "1.1rem", marginTop: "10px" }}>B.Tech Total Institute Capacity</h3>
              <p style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: "15px" }}>
                Enter the absolute total number of seats available for each programme. This is used to calculate vacancies for Sliding Rounds.
              </p>
              <table className="table table-bordered" style={{ backgroundColor: "white" }}>
                <thead>
                  <tr>
                    <th>Programme</th>
                    <th>Total Seats</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>B.Tech CSE</td>
                    <td>
                      <input type="number" className="form-control" value={dataToSave["btech_cse_total_seats"] || ""} onChange={(e) => setDataToSave({ ...dataToSave, btech_cse_total_seats: parseInt(e.target.value) || 0 })} />
                    </td>
                  </tr>
                  <tr>
                    <td>B.Tech ECE</td>
                    <td>
                      <input type="number" className="form-control" value={dataToSave["btech_ece_total_seats"] || ""} onChange={(e) => setDataToSave({ ...dataToSave, btech_ece_total_seats: parseInt(e.target.value) || 0 })} />
                    </td>
                  </tr>
                  <tr>
                    <td>B.Tech AI&amp;DS</td>
                    <td>
                      <input type="number" className="form-control" value={dataToSave["btech_ai_total_seats"] || ""} onChange={(e) => setDataToSave({ ...dataToSave, btech_ai_total_seats: parseInt(e.target.value) || 0 })} />
                    </td>
                  </tr>
                  <tr>
                    <td>Integrated M.Tech CSE</td>
                    <td>
                      <input type="number" className="form-control" value={dataToSave["imtech_cse_total_seats"] || ""} onChange={(e) => setDataToSave({ ...dataToSave, imtech_cse_total_seats: parseInt(e.target.value) || 0 })} />
                    </td>
                  </tr>
                  <tr>
                    <td>Integrated M.Tech ECE</td>
                    <td>
                      <input type="number" className="form-control" value={dataToSave["imtech_ece_total_seats"] || ""} onChange={(e) => setDataToSave({ ...dataToSave, imtech_ece_total_seats: parseInt(e.target.value) || 0 })} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={styles.buttonRow}>
          <button
            className={styles.primaryBtn}
            disabled={!canSubmit}
            onClick={handleCreate}
          >
            Submit
          </button>
        </div>
      </div>

      {/* Success Toast */}
      {showToast && (
        <div className="toast-container position-fixed mt-5 start-50 translate-middle p-3">
          <div className="toast show">
            <div className="toast-header">
              <strong className="me-auto">Success</strong>
              <button
                className="btn-close"
                onClick={() => setShowToast(false)}
              />
            </div>
            <div className="toast-body text-success text-center">
              Created successfully!
            </div>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {showErrorToast && (
        <div className="toast-container position-fixed mt-5 start-50 translate-middle p-3">
          <div className="toast show">
            <div className="toast-header">
              <strong className="me-auto text-danger">Error</strong>
              <button
                className="btn-close"
                onClick={() => setShowErrorToast(false)}
              />
            </div>
            <div className="toast-body text-danger text-center">
              {errorMsg}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateDrive;